/* eslint-disable react/jsx-props-no-spreading */
import {useTranslation} from 'react-i18next';
import {useMutation} from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import {toast} from 'react-toastify';
import {checkOrgAdmin, checkPermission, getUserOrg, messageErrors} from 'utils/utils';
import useUser from 'hooks/useUser';
import {Modal, Radio, Table, TextInput} from 'flowbite-react';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {useForm} from 'react-hook-form';
import {useEffect, useRef, useState} from 'react';
import {themModal} from 'utils/theme';
import '../../form/styles.scss';
import './itemModal.scss';
import MultiSelect from 'components/select/index';
import InputText from 'components/commonComponent/inputText';
import {orgSelector, userSelector} from 'redux/selectors';
import {useSelector} from 'react-redux';
import {getCategoriesByFilter} from 'api/categoryApi';
import {integrationVendors, pageType, permissionKeys, sortByTypeType, defaultpageCount} from 'utils/constants';
import {createItem, getItemsByFilter, updateItemById} from 'api/itemApi';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import {HiOutlineBan, HiOutlinePencil, HiOutlineRefresh, HiOutlineTrash, HiPlus} from 'react-icons/hi';
import {v4 as uuidv4} from 'uuid';
import {cloneDeep, concat} from 'lodash';
import { getRegionByFilter, getChildrenRegionByParent } from 'api/regionApi';
import { getOrgVendorsByFilter } from 'api/vendorApi';
import Flatpickr from 'react-flatpickr';
import {HiOutlineCalendar, HiOutlineX} from 'react-icons/hi';

type itemModalProps = {
    openModal: boolean,
    setOpenModal: Function,
    headerTitle?: string,
    targetData?: any,
    isEdit: boolean,
    queryClient?: any,
    setCurrentPage?: any,
    currentPage?: number,
};
const ItemModal = (props: itemModalProps) => {
    const {openModal, targetData, isEdit, setOpenModal, headerTitle, currentPage, queryClient, setCurrentPage} = props;
    const {fetchCurrentUser} = useUser();
    const [t] = useTranslation();
    const rootRef = useRef(null);
    const {userInfo} = useSelector(userSelector);
    const {WRITE_USER, PLATFORM_ADMIN} = permissionKeys;
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [categoryTypeOptions, setCategoryTypeOptions] = useState([{label: '...', value: ''}]);
    const [selectedVendor, setSelectedVendor] = useState<any>(null);
    const [vendorTypeOptions, setVendorTypeOptions] = useState<any>([]);
    const [backupItemOptions, setBackupItemOptions] = useState<any>([]);
    const [selectedBackupItem, setSelectedBackupItem] = useState<any>(null);
    const [vendorConfigs, setVendorConfigs] = useState<Record<string, { vendorSku?: string; unitOfMeasure?: string }>>({});
    const [error, setErrors] = useState(false);
    const {organizationId} = useSelector(orgSelector);
    const [upcs, setUpcs] = useState<any>([]);
    const [regionTypeOptions, setRegionTypeOptions] = useState([{label: '...', value: ''}]);
    const [selectedRegion, setSelectedRegion] = useState([]);
    const [seasonalEnabled, setSeasonalEnabled] = useState(false);
    const [seasonalStartDate, setSeasonalStartDate] = useState('');
    const [seasonalEndDate, setSeasonalEndDate] = useState('');
    const [seasonalYear, setSeasonalYear] = useState('');
    const startDateRef = useRef<any>(null);
    const endDateRef = useRef<any>(null);
    const isAdmin = checkPermission(userInfo, pageType.ORGANIZATION, [PLATFORM_ADMIN], organizationId);
    const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, isAdmin);
    const isMusclefoodVendor = (option: any) => {
        if (option?.integration === integrationVendors.MUSCLEFOODS) return true;
        const normalizedVendorName = (option?.label || '').trim().toLowerCase();
        return normalizedVendorName === 'musclefood' || normalizedVendorName === 'musclefoods';
    };
    const showUnitOfMeasureField = !!selectedVendor?.value && isMusclefoodVendor(selectedVendor);

    const ValidateSchema = Yup.object().shape({
        name: Yup.string().required(t('userManagementPage.requiredField')).max(150, t('locationModal.maxLength150')),
        sku: Yup.string().required(t('requiredField')),
        backupSku: Yup.string().notRequired(),
        vendorSku: Yup.string().when('organizationVendorId', {
            is: (organizationVendorId: any) => !!organizationVendorId,
            then: schema => schema.trim().required(t('requiredField')),
            otherwise: schema => schema.notRequired(),
        }),
        unitOfMeasure: Yup.string().notRequired(),
    });

    const hookForm = useForm<any>({
        mode: 'onChange',
        resolver: yupResolver(ValidateSchema),
    });

    const {register, handleSubmit, setValue, clearErrors, setError, reset, getValues} = hookForm;
    const isVendorsFetchStartedRef = useRef(false);

    useEffect(() => {
        if (isVendorsFetchStartedRef.current) {
            return;
        }
        isVendorsFetchStartedRef.current = true;

        const pageSize = 100;

        const buildHierarchicalCategoryOptions = (items: any[]) => {
            const getParentId = (i: any) => i.parentId || i.parent?.id || null;
            const parents = items.filter((i: any) => !getParentId(i));
            const children = items.filter((i: any) => getParentId(i));
            const result: any[] = [];
            parents.sort((a: any, b: any) => a.name.localeCompare(b.name)).forEach((p: any) => {
                result.push({ label: p.name, value: p.id });
                children
                    .filter((c: any) => getParentId(c) === p.id)
                    .sort((a: any, b: any) => a.name.localeCompare(b.name))
                    .forEach((c: any) => {
                        result.push({ label: `↳ ${c.name}`, value: c.id });
                    });
            });
            const parentIds = new Set(parents.map((p: any) => p.id));
            children.forEach((c: any) => {
                if (!parentIds.has(getParentId(c))) {
                    result.push({ label: `↳ ${c.name}`, value: c.id });
                }
            });
            return result;
        };

        getCategoriesByFilter({ page: 0, limit: pageSize, sortByType: sortByTypeType.DESC, organizationId })?.then(res => {
            const totalCount = res?.data?.totalEntities;
            if (totalCount === 0) {
              setCategoryTypeOptions([]);
            } else {
              const pageBreak = Math.ceil(totalCount / pageSize);
              let listTemps: any = [];
              for (let index = 0; index < pageBreak; index++) {
                // eslint-disable-next-line
                getCategoriesByFilter({ page: index, limit: pageSize, sortByType: sortByTypeType.DESC, organizationId })?.then(resPage => {
                  if (listTemps.length === 0) {
                    listTemps = resPage?.data?.entities;
                  } else {
                    listTemps = listTemps.concat(resPage?.data?.entities);
                  }
      
                  if (listTemps.length > 0) {
                    setCategoryTypeOptions(buildHierarchicalCategoryOptions(listTemps));
                  }
                });
              }
            }
          });

        getRegionByFilter({page: 0, limit: pageSize, sortByType: sortByTypeType.DESC, organizationId, userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId})?.then(res => {
            const totalCount = res?.data?.totalEntities;
            if (totalCount === 0) {
                setRegionTypeOptions([]);
            } else {
                const pageBreak = Math.ceil(totalCount / pageSize);
                let listTemps: any = [];
                for (let index = 0; index < pageBreak; index++) {
                    // eslint-disable-next-line
                    getRegionByFilter({page: index, limit: pageSize, sortByType: sortByTypeType.DESC, organizationId, userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId})?.then(resPage => {
                      if (listTemps.length === 0) {
                        listTemps = resPage?.data?.entities;
                      } else {
                        listTemps = listTemps.concat(resPage?.data?.entities);
                      }
          
                      if (listTemps.length > 0) {
                        const newOption = listTemps.map((i: any) => ({ label: i.name, value: i.id }));
                        setRegionTypeOptions(newOption);
                      }
                    });
                }
            }
        });

        getOrgVendorsByFilter({page: 0, limit: 0, sortByType: sortByTypeType.DESC, organizationId})?.then(res => {
            const newOption = res?.data?.entities?.map((i: any) => ({
                label: i.name,
                value: i.id,
                integration: i.integration,
            }));
            setVendorTypeOptions(newOption);
        });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isEdit) {
            reset(targetData);
            const savedVendorConfig = targetData?.vendorConfig || {};
            setVendorConfigs(savedVendorConfig);
            setValue('name', targetData.name);
            const categoryId = targetData.category?.id;
            if (categoryId) {
                const cateSelect: any = categoryTypeOptions.find(c => c.value === categoryId);
                setSelectedCategory(cateSelect);
            }
            setValue('categoryId', categoryId);
            const organizationVendorId = targetData.organizationVendorId || targetData?.organizationVendor?.id;
            if (organizationVendorId) {
                const vendorSelect: any = vendorTypeOptions.find((c: any) => c.value === organizationVendorId);
                setSelectedVendor(vendorSelect);
                const vendorConfigById = savedVendorConfig?.[organizationVendorId] || { vendorSku: '', unitOfMeasure: '' };
                setValue('vendorSku', vendorConfigById?.vendorSku || '');
                setValue('unitOfMeasure', vendorConfigById?.unitOfMeasure || '');
            } else {
                setValue('vendorSku', '');
                setValue('unitOfMeasure', '');
            }
            setValue('organizationVendorId', organizationVendorId);
            setValue('backupSku', targetData.backupSku || '');
            setValue('sku', targetData.sku);
            setValue('unitPrice', targetData.unitPrice);
            setValue('caseCost', targetData.caseCost);
            setValue('maxOrderQuantity', targetData.maxOrderQuantity);
            setUpcs(targetData.upcs);
            const hasSeasonalDates = !!(targetData?.seasonalStartDate && targetData?.seasonalEndDate);
            setSeasonalEnabled(hasSeasonalDates);
            setSeasonalStartDate(targetData?.seasonalStartDate || '');
            setSeasonalEndDate(targetData?.seasonalEndDate || '');
            setSeasonalYear(targetData?.seasonalYear != null ? String(targetData.seasonalYear) : '');
            if (targetData?.notAvailableAts) {
                const regionSelect: any = regionTypeOptions?.filter((x: any) => targetData?.notAvailableAts?.includes(x.value));
                setSelectedRegion(regionSelect);
                setValue("notAvailableAts", regionSelect?.map((x: any) => { return x.value}));
            }
        } else {
            reset();
            setVendorConfigs({});
            setValue('vendorSku', '');
            setValue('unitOfMeasure', '');
            setSeasonalEnabled(false);
            setSeasonalStartDate('');
            setSeasonalEndDate('');
            setSeasonalYear('');
            if (vendorTypeOptions && vendorTypeOptions.length === 1) {
                const vendorSelect: any = vendorTypeOptions[0];
                setSelectedVendor(vendorSelect);
                setValue('organizationVendorId', vendorSelect?.value);
            }
        }

        // eslint-disable-next-line
    }, [categoryTypeOptions, vendorTypeOptions]);

    const buildSeasonalPayload = () => {
        if (!seasonalEnabled || !seasonalStartDate || !seasonalEndDate) {
            return { seasonalStartDate: null, seasonalEndDate: null, seasonalYear: null };
        }
        const parsedYear = seasonalYear ? parseInt(seasonalYear, 10) : null;
        return {
            seasonalStartDate,
            seasonalEndDate,
            seasonalYear: !isNaN(parsedYear as number) && parsedYear ? parsedYear : null,
        };
    };

    const handleAddAndUpdateItem = async (payload: any) => {
        if (payload.sku) payload.sku = payload.sku.trim();
        if (isEdit) {
            payload.organizationId = organizationId;
            payload.organization = {id: organizationId};
            payload.category = {id: payload.categoryId};
            if (payload.organizationVendorId === '' || payload.organizationVendorId === 'null' || payload.organizationVendorId === null) {
                payload.organizationVendorId = null;
            }
            delete payload.vendorId;
            delete payload.vendor;
            payload.vendorConfig = getUpdatedVendorConfig(payload);
            delete payload.vendorSku;
            delete payload.unitOfMeasure;
            payload.unitPrice = parseFloat(payload.unitPrice);
            payload.caseCost = parseFloat(payload.caseCost);
            payload.maxOrderQuantity = parseInt(payload.maxOrderQuantity);
            const upcDefault = upcs?.find((x: any) => x.default === true);
            const caseQuantity = upcDefault ? parseInt(upcDefault.caseQuantity) : 0;
            payload.unitCost = caseQuantity > 0 ? payload.caseCost / caseQuantity : 0;
            payload.upcs = upcs;
            Object.assign(payload, buildSeasonalPayload());
            await updateItemById(targetData.id, payload);
        } else {
            payload.organizationId = organizationId;
            payload.organization = {id: organizationId};
            payload.category = {id: payload.categoryId};
            if (payload.organizationVendorId === '' || payload.organizationVendorId === 'null' || payload.organizationVendorId === null) {
                payload.organizationVendorId = null;
            }
            delete payload.vendorId;
            delete payload.vendor;
            payload.vendorConfig = getUpdatedVendorConfig(payload);
            delete payload.vendorSku;
            delete payload.unitOfMeasure;
            payload.unitPrice = parseFloat(payload.unitPrice);
            payload.caseCost = parseFloat(payload.caseCost);
            payload.maxOrderQuantity = parseInt(payload.maxOrderQuantity);
            payload.isActive = true;
            const upcDefault = upcs?.find((x: any) => x.default === true);
            const caseQuantity = upcDefault ? parseInt(upcDefault.caseQuantity) : 0;
            payload.unitCost = caseQuantity > 0 ? payload.caseCost / caseQuantity : 0;
            payload.upcs = upcs;
            Object.assign(payload, buildSeasonalPayload());
            await createItem(payload);
        }
    };

    const mutation = useMutation(['create-update-item'], {mutationFn: handleAddAndUpdateItem});

    const onSubmit = (values: any, props: any) => {
        const vendorId = values?.organizationVendorId || selectedVendor?.value;
        const vendorSku = (values?.vendorSku || '').trim();
        const unitOfMeasure = (values?.unitOfMeasure || '').trim();
        const selectedVendorOption = selectedVendor?.value === vendorId
            ? selectedVendor
            : vendorTypeOptions.find((option: any) => option.value === vendorId);
        const requiresUnitOfMeasure = !!vendorId && isMusclefoodVendor(selectedVendorOption);
        if ((vendorId && !vendorSku) || (requiresUnitOfMeasure && !unitOfMeasure)) {
            if (!vendorSku) {
                setError('vendorSku', { type: 'required', message: t('requiredField') });
            }
            if (requiresUnitOfMeasure && !unitOfMeasure) {
                setError('unitOfMeasure', { type: 'required', message: t('requiredField') });
            }
            toast.error(String(t('itemPage.requiredFieldMessage')));
            return;
        }
        clearErrors(['backupSku', 'vendorSku', 'unitOfMeasure']);
        mutation.mutate(values, {
            onSuccess: () => {
                toggleHandler();
                if (currentPage !== 0) {
                    queryClient.invalidateQueries('getItems', {refetchActive: false}, {cancelRefetch: true});
                    setCurrentPage(0);
                } else queryClient.invalidateQueries('getItems', {refetchActive: true}, {cancelRefetch: true});
                const message: string = isEdit ? t('itemPage.editSuccess') : t('itemPage.saveSuccess');
                toast.success(message);
                fetchCurrentUser();
            },
            onError: error => {
                const message: string = messageErrors(error, t);
                toast.error(message);
            },
        });
    };

    const handleChangeCategory = (option: any) => {
        setSelectedCategory(option);
        setValue('categoryId', option?.value || null);
        setSelectedBackupItem(null);
        setValue('backupSku', '');
    };

    const persistCurrentVendorConfigFromForm = () => {
        const currentVendorId = selectedVendor?.value;
        if (!currentVendorId) return;
        const currentVendorSku = getValues('vendorSku') || '';
        const currentUnitOfMeasure = getValues('unitOfMeasure') || '';
        setVendorConfigs(prev => ({
            ...prev,
            [currentVendorId]: {
                ...(prev[currentVendorId] || {}),
                vendorSku: currentVendorSku,
                unitOfMeasure: currentUnitOfMeasure,
            },
        }));
    };

    const getUpdatedVendorConfig = (payload: any) => {
        const vendorId = payload?.organizationVendorId || selectedVendor?.value;
        if (!vendorId) return vendorConfigs;
        return {
            ...vendorConfigs,
            [vendorId]: {
                ...(vendorConfigs[vendorId] || {}),
                vendorSku: payload?.vendorSku || '',
                unitOfMeasure: payload?.unitOfMeasure || '',
            },
        };
    };

    const handleChangeVendor = (option: any) => {
        persistCurrentVendorConfigFromForm();
        if (!option || option.value === null || option.value === '') {
            setSelectedVendor(null);
            setValue('organizationVendorId', null as any, { shouldValidate: true, shouldDirty: true });
            setSelectedBackupItem(null);
            setValue('backupSku', '');
            setValue('vendorSku', '');
            setValue('unitOfMeasure', '');
            clearErrors(['vendorSku', 'unitOfMeasure']);
            return;
        }

        setSelectedBackupItem(null);
        setValue('backupSku', '');
        setSelectedVendor(option);
        setValue('organizationVendorId', option.value, { shouldValidate: true, shouldDirty: true });
        const vendorConfigById = vendorConfigs?.[option.value] || { vendorSku: '', unitOfMeasure: '' };
        setValue('vendorSku', vendorConfigById?.vendorSku || '');
        setValue('unitOfMeasure', vendorConfigById?.unitOfMeasure || '');
        clearErrors('unitOfMeasure');
    };

    const handleChangeBackupItem = (option: any) => {
        if (!option || !option.value?.sku) {
            setSelectedBackupItem(null);
            setValue('backupSku', '');
            return;
        }

        setSelectedBackupItem(option);
        setValue('backupSku', option.value.sku);
        clearErrors('backupSku');
    };

    const handleChangeRegion = async (options: any) => {
        if (!options || options.length === 0) {
            setSelectedRegion([]);
            setValue('notAvailableAts', []);
            return;
        }

        // Preserve exactly what react-select reports so user removals are respected
        const selectValues: any = [...options];

        // Auto-add children only for regions that are newly selected
        const newlyAdded = options.filter(
            (opt: any) => !selectedRegion?.find((x: any) => x.value === opt.value)
        );

        for (const element of newlyAdded) {
            const resChild = await getChildrenRegionByParent(element.value);
            if (resChild?.data?.length > 0) {
                for (const child of resChild.data) {
                    const selectValue = regionTypeOptions?.find((x: any) => x.value === child.id);
                    if (selectValue && !selectValues.find((x: any) => x.value === selectValue.value)) {
                        selectValues.push(selectValue);
                    }
                }
            }
        }

        setSelectedRegion(selectValues);
        setValue('notAvailableAts', selectValues.map((x: any) => x.value));
    };

    const toggleHandler = () => {
        setOpenModal(!openModal);
        reset();
        setSeasonalEnabled(false);
        setSeasonalStartDate('');
        setSeasonalEndDate('');
        setSeasonalYear('');
    };

    const handleAddUpc = () => {
        const check = upcs?.find((x: any) => x.upcCode === '');
        if (check) {
            toast.error(`${t('itemPage.upcs.requiredInputUpc')}`);
        } else {
            const newRow: any = {
                id: uuidv4(),
                upcCode: '',
                caseQuantity: 0,
                default: false
            };
            const newData: any = concat(upcs, newRow);
            setUpcs(newData);
        }
    };

    const handleDeleteRow = (id: string) => {
        const newData = upcs?.filter((d: any) => d.id !== id);
        setUpcs(newData);
    }

    const handleChangeContentOverview = async (id: string, value: any, field: any) => {
      const listContentClone = cloneDeep(upcs);
      const findContent: any = listContentClone?.find((item: any) => item.id === id);
      if (field === 'caseQuantity') {
        findContent[field] = parseInt(value);
      }
      if (field === 'default') {
          if (value === true) {
            findContent[field] = value;
            const listContentChecked: any = listContentClone?.filter((item: any) => item.id !== id);
            listContentChecked?.forEach((x: any) => {
              x.default = false;
            });
          } else {
            findContent[field] = value;
          }
      } else {
        findContent[field] = field === 'upcCode' ? value?.trim() : value;
      }
      setUpcs(listContentClone);
    };

    const customStyles = {
        menu: (provided: any) => ({
          ...provided,
          zIndex: 2,
        }),
        menuPortal: (provided: any) => ({
          ...provided,
          zIndex: 2,
        }),
      };
    const hasSelectedVendor = Boolean(selectedVendor?.value);

    useEffect(() => {
        const selectedVendorId = (selectedVendor as any)?.value;
        if (!organizationId || !selectedVendorId) {
            setBackupItemOptions([]);
            setSelectedBackupItem(null);
            setValue('backupSku', '');
            return;
        }

        const fetchBackupItemsByVendor = async () => {
            const pageSize = 100;
            const firstRes = await getItemsByFilter({
                page: 0,
                limit: pageSize,
                sortByType: sortByTypeType.DESC,
                organizationId,
                organizationVendorId: selectedVendorId,
            });

            const totalCount = firstRes?.data?.totalEntities || 0;
            let listTemps: any = firstRes?.data?.entities || [];
            const pageBreak = Math.ceil(totalCount / pageSize);
            for (let index = 1; index < pageBreak; index++) {
                const resPage = await getItemsByFilter({
                    page: index,
                    limit: pageSize,
                    sortByType: sortByTypeType.DESC,
                    organizationId,
                    organizationVendorId: selectedVendorId,
                });
                listTemps = listTemps.concat(resPage?.data?.entities || []);
            }

            const relatedVendorItems = listTemps.filter((item: any) => {
                return item?.organizationVendorId === selectedVendorId && item?.id !== targetData?.id;
            });

            const newBackupOptions = relatedVendorItems.map((item: any) => ({
                label: `${item.name} (${item.sku})`,
                value: {
                    id: item.id,
                    sku: item.sku,
                },
            }));
            setBackupItemOptions(newBackupOptions);

            const backupSkuValue = getValues('backupSku');
            if (backupSkuValue) {
                const selected = newBackupOptions.find((opt: any) => opt.value?.sku === backupSkuValue);
                setSelectedBackupItem(selected || null);
            } else {
                setSelectedBackupItem(null);
            }
        };

        fetchBackupItemsByVendor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVendor, organizationId, targetData?.id]);

    return (
        <div ref={rootRef} className="item-modal-container">
            <Modal
                show={openModal}
                size="4xl"
                className="item-modal"
                root={rootRef.current ?? undefined}
                onClose={toggleHandler}
                theme={themModal}
            >
                <CustomModalHeader title={headerTitle} toggle={toggleHandler}/>
                <Modal.Body theme={{base: 'flex-12 pb-4 pt-3 px-12'}}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mt-2 relative flex md:flex-row flex-col">
                            <InputText hookForm={hookForm} placeholder={`${t('itemPage.modal.modalItemName')}`}
                                       name="name" id="name" className="md:mr-4 mb-2 md:mb-0" isHolderLabel={true}/>
                            <MultiSelect
                                className="mb-2 md:mb-0"
                                onChange={handleChangeRegion}
                                options={regionTypeOptions}
                                value={selectedRegion}
                                hookForm={hookForm}
                                name="regionId"
                                isMulti={true}
                                noOptionsMessage={() => t('No options')}
                                closeMenuOnSelect
                                isHolderLabel={true}
                                placeholder={`${t('itemPage.modal.modalNotAvailableAts')}`}
                                customStyles={customStyles}
                            />
                        </div>
                        <div className="mt-4 md:mt-2 relative flex md:flex-row flex-col">
                            <MultiSelect
                                className="md:mr-4 mb-2 md:mb-0"
                                onChange={handleChangeCategory}
                                options={categoryTypeOptions}
                                value={selectedCategory}
                                hookForm={hookForm}
                                name="categoryId"
                                noOptionsMessage={() => t('No options')}
                                closeMenuOnSelect
                                isHolderLabel={true}
                                placeholder={`${t('itemPage.modal.modalCategory')}`}
                                customStyles={customStyles}
                            />
                            <InputText hookForm={hookForm} placeholder={`${t('itemPage.modal.modalSku')}`} name="sku"
                                       id="sku" className="mb-2 md:mb-0" isHolderLabel={true}/>
                        </div>
                        <div className="mt-4 md:mt-2 relative flex md:flex-row flex-col">
                            <InputText
                                hookForm={hookForm}
                                placeholder={`${t('itemPage.modal.modalSalePrice')}`}
                                type="number"
                                step="0.01"
                                name="unitPrice"
                                id="unitPrice"
                                isHolderLabel={true}
                                className="md:mr-4 mb-2 md:mb-0"
                            />
                            <InputText hookForm={hookForm} placeholder={`${t('itemPage.modal.modalCaseCost')}`}
                                       className="mb-2 md:mb-0" type="number" step="0.01" name="caseCost" id="caseCost"
                                       isHolderLabel={true}/>
                        </div>
                        <div className="mt-4 md:mt-2 relative flex md:flex-row flex-col">
                            <MultiSelect
                                onChange={handleChangeVendor}
                                options={vendorTypeOptions}
                                value={selectedVendor}
                                hookForm={hookForm}
                                name="organizationVendorId"
                                noOptionsMessage={() => t('No options')}
                                closeMenuOnSelect
                                isHolderLabel={true}
                                placeholder={`${t('itemPage.modal.modalVendor')}`}
                                className="md:mr-4 mb-2 md:mb-0"
                                />
                            <InputText
                                hookForm={hookForm}
                                placeholder={`${t('itemPage.modal.maxOrderQuantity')}`}
                                type="number"
                                step="1"
                                name="maxOrderQuantity"
                                id="maxOrderQuantity"
                                isHolderLabel={true}
                            />
                        </div>
                        {hasSelectedVendor && (
                            <>
                                <div className="mt-4 md:mt-2 relative flex md:flex-row flex-col">
                                    <div className="flex-1 md:mr-4 mb-2 md:mb-0">
                                        <MultiSelect
                                            onChange={handleChangeBackupItem}
                                            options={backupItemOptions}
                                            value={selectedBackupItem}
                                            hookForm={hookForm}
                                            name="backupSku"
                                            noOptionsMessage={() => t('itemPage.modal.modalCreateBackupItem')}
                                            closeMenuOnSelect
                                            isHolderLabel={true}
                                        placeholder={`${t('itemPage.modal.modalBackupItem')}`}
                                            customStyles={customStyles}
                                        />
                                    </div>
                                    <InputText
                                        hookForm={hookForm}
                                        placeholder={`${t('itemPage.modal.modalVendorSku')} *`}
                                        name="vendorSku"
                                        id="vendorSku"
                                        className={`${showUnitOfMeasureField ? 'md:mr-4' : ''} mb-2 md:mb-0`}
                                        isHolderLabel={true}
                                    />
                                    {showUnitOfMeasureField && (
                                        <InputText
                                            hookForm={hookForm}
                                            placeholder={`${t('itemPage.modal.modalUnitOfMeasure')} *`}
                                            name="unitOfMeasure"
                                            id="unitOfMeasure"
                                            isHolderLabel={true}
                                        />
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {t('itemPage.modal.modalCreateBackupItem')}
                                </div>
                            </>
                        )}
                        {/* Disable Ordering */}
                        <div className="mt-4 md:mt-3">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isOrderingDisabled"
                                    {...register('isOrderingDisabled')}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                                />
                                <label htmlFor="isOrderingDisabled" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                                    {t('itemPage.modal.disableOrdering')}
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 ml-6">{t('itemPage.modal.disableOrderingHint')}</p>
                        </div>
                        {/* Seasonal Availability */}
                        <div className="mt-4 md:mt-3 seasonal-section">
                            <div className="flex items-center gap-2 mb-2">
                                <input
                                    type="checkbox"
                                    id="seasonalEnabled"
                                    checked={seasonalEnabled}
                                    onChange={(e) => {
                                        setSeasonalEnabled(e.target.checked);
                                        if (!e.target.checked) {
                                            setSeasonalStartDate('');
                                            setSeasonalEndDate('');
                                            setSeasonalYear('');
                                        }
                                    }}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                                />
                                <label htmlFor="seasonalEnabled" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                                    {t('itemPage.modal.enableSeasonalWindow')}
                                </label>
                            </div>
                            {seasonalEnabled && (
                                <div className="seasonal-fields border border-gray-200 rounded-md p-3 bg-gray-50">
                                    <p className="text-xs text-gray-500 mb-3">{t('itemPage.modal.seasonalDateHint')}</p>
                                    <div className="flex md:flex-row flex-col gap-3">
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                {t('itemPage.modal.seasonalStartDate')}
                                            </label>
                                            <div className="custom-date-range-modal relative">
                                                <Flatpickr
                                                    ref={startDateRef}
                                                    value={seasonalStartDate}
                                                    onChange={(dates: Date[]) => {
                                                        if (dates[0]) {
                                                            const d = dates[0];
                                                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                            const dd = String(d.getDate()).padStart(2, '0');
                                                            setSeasonalStartDate(`${mm}/${dd}`);
                                                        }
                                                    }}
                                                    options={{ mode: 'single', dateFormat: 'm/d', disableMobile: true }}
                                                    placeholder="MM/DD"
                                                    className="seasonal-date-input"
                                                />
                                                {seasonalStartDate ? (
                                                    <button type="button" onClick={() => { setSeasonalStartDate(''); startDateRef.current?.flatpickr?.clear(); }}>
                                                        <HiOutlineX />
                                                    </button>
                                                ) : (
                                                    <HiOutlineCalendar />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                {t('itemPage.modal.seasonalEndDate')}
                                            </label>
                                            <div className="custom-date-range-modal relative">
                                                <Flatpickr
                                                    ref={endDateRef}
                                                    value={seasonalEndDate}
                                                    onChange={(dates: Date[]) => {
                                                        if (dates[0]) {
                                                            const d = dates[0];
                                                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                            const dd = String(d.getDate()).padStart(2, '0');
                                                            setSeasonalEndDate(`${mm}/${dd}`);
                                                        }
                                                    }}
                                                    options={{ mode: 'single', dateFormat: 'm/d', disableMobile: true }}
                                                    placeholder="MM/DD"
                                                    className="seasonal-date-input"
                                                />
                                                {seasonalEndDate ? (
                                                    <button type="button" onClick={() => { setSeasonalEndDate(''); endDateRef.current?.flatpickr?.clear(); }}>
                                                        <HiOutlineX />
                                                    </button>
                                                ) : (
                                                    <HiOutlineCalendar />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                {t('itemPage.modal.seasonalYear')}
                                            </label>
                                            <input
                                                type="number"
                                                value={seasonalYear}
                                                onChange={(e) => setSeasonalYear(e.target.value)}
                                                placeholder="e.g. 2026"
                                                min="2000"
                                                max="2100"
                                                className="seasonal-year-input w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">{t('itemPage.modal.seasonalYearHint')}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Add UPCs */}
                            <GroupButtonWithIcons
                                className="add-UPCs"
                                buttons={[
                                    {
                                        type: 'button',
                                        text: `${t('itemPage.upcs.add')}`,
                                        classType: 'white',
                                        icon: <HiPlus size={"18px"}/>,
                                        action: () => handleAddUpc(),
                                    }
                                ]}
                            />
                        {/* Table */}
                        {upcs?.length > 0 && (
                            <Table className="upcs-table">
                                <Table.Head className="text-xs text-gray-900 text-xs border-b-2 border-gray-100">
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium px-2 py-1 flex-1"
                                    >
                                        {t('itemPage.upcs.upcCode')}
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium px-2 py-1"
                                    >
                                        {t('itemPage.upcs.quantity')}
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium px-2 py-1"
                                    >
                                        {t('itemPage.upcs.default')}
                                    </Table.HeadCell>

                                </Table.Head>
                                <Table.Body>
                                    {upcs.map((item: any, index: number) => (
                                        <>
                                            <Table.Row key={`row-${index + 1}`}
                                                       className="bg-white cursor-pointer text-sm hover:bg-gray-50 border-b border-gray-100">
                                                <Table.Cell className="font-semibold py-[8px] px-2 custom-input">
                                                    <TextInput
                                                        type="text"
                                                        id={`upc-${index}`}
                                                        name={`upc-${index}`}
                                                        value={item?.upcCode}
                                                        onChange={(e: any) => {
                                                            handleChangeContentOverview(item?.id, e.target.value, 'upcCode');
                                                        }}
                                                    />
                                                </Table.Cell>
                                                <Table.Cell
                                                    className="font-semibold py-[8px] w-[200px] px-2 custom-input">
                                                    <TextInput
                                                        type="number"
                                                        id={`caseQuantity-${index}`}
                                                        name={`caseQuantity-${index}`}
                                                        value={item?.caseQuantity}
                                                        onChange={(e: any) => {
                                                            handleChangeContentOverview(item?.id, e.target.value, 'caseQuantity');
                                                        }}
                                                    />
                                                </Table.Cell>

                                                <Table.Cell className="py-[8px] px-2 w-[100px] ">
                                                        <Radio
                                                            id={`default-${index}`}
                                                            name={`default-${index}`}
                                                            value={item?.default}
                                                            checked={item?.default}
                                                            onChange={(e: any) => {
                                                                handleChangeContentOverview(item?.id, e.target.checked, 'default');
                                                            }}
                                                        />
                                                </Table.Cell>
                                                <Table.Cell className="py-[8px] px-2">
                                                    <GroupButtonWithIcons
                                                        className="items-center justify-center"
                                                        buttons={[
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlineTrash size={"20px"}/>,
                                                                action: () => handleDeleteRow(item?.id)
                                                            },
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiPlus size={"20px"}/>,
                                                                action: () => handleAddUpc()
                                                            },
                                                        ]}
                                                    />
                                                </Table.Cell>
                                            </Table.Row>
                                        </>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                        <GroupButton
                            className="items-center justify-center pt-5 mb-4"
                            buttons={[
                                {
                                    type: 'button',
                                    text: t('modal.cancel'),
                                    classType: 'white',
                                    action: () => toggleHandler(),
                                },
                                {
                                    type: 'submit',
                                    text: t('modal.save'),
                                    classType: 'blue',
                                    isLoading: mutation.isLoading,
                                },
                            ]}
                        />
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
};
export default ItemModal;
